/**
 * Pagination - Componente de paginación para tablas
 * 
 * Botones de tamaño de página (10, 25, 50, Todos) centrados.
 * Texto informativo "Mostrando X de Y" a la derecha.
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 1.0.0
 */

import React from 'react';
import { 
  colors, 
  typography, 
  borderRadius,
  transitions,
} from '@/design-system/v2';

export interface PaginationProps {
  /** Tamaño de página actual */
  pageSize: number | 'all';
  /** Opciones de tamaño disponibles */
  pageSizeOptions?: (number | 'all')[];
  /** Total de elementos */
  totalItems: number;
  /** Elementos mostrados actualmente */
  visibleItems: number;
  /** Callback al cambiar tamaño de página */
  onPageSizeChange?: (size: number | 'all') => void;
}

const DEFAULT_OPTIONS: (number | 'all')[] = [10, 25, 50, 'all'];

const STYLES = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
    paddingTop: '12px',
    position: 'relative' as const,
    fontFamily: typography.fontFamily.sans,
  } as React.CSSProperties,
  
  pageSizeGroup: {
    display: 'flex',
    gap: '10px',
  } as React.CSSProperties,
  
  button: {
    padding: '6px 10px',
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    color: colors.fg.secondary,
    fontFamily: 'inherit',
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
    transition: transitions.base,
  } as React.CSSProperties,
  
  buttonActive: {
    background: colors.primary,
    borderColor: colors.primary,
    color: colors.bg.primary,
  } as React.CSSProperties,
  
  info: {
    position: 'absolute' as const,
    right: 0,
    fontSize: typography.fontSize.sm,
    color: colors.fg.secondary,
  } as React.CSSProperties,
};

export const Pagination: React.FC<PaginationProps> = ({
  pageSize,
  pageSizeOptions = DEFAULT_OPTIONS,
  totalItems,
  visibleItems,
  onPageSizeChange,
}) => {
  const [hoveredButton, setHoveredButton] = React.useState<number | 'all' | null>(null);

  const getButtonStyle = (option: number | 'all'): React.CSSProperties => {
    const isActive = pageSize === option;
    const isHovered = hoveredButton === option && !isActive;
    
    return {
      ...STYLES.button,
      ...(isActive ? STYLES.buttonActive : {}),
      ...(isHovered ? { 
        borderColor: colors.fg.muted, 
        color: colors.fg.primary 
      } : {}),
    };
  };

  const getLabel = (option: number | 'all'): string => {
    return option === 'all' ? 'Todos' : String(option);
  };

  return (
    <div style={STYLES.container}>
      <div style={STYLES.pageSizeGroup}>
        {pageSizeOptions.map((option) => (
          <button
            key={option}
            style={getButtonStyle(option)}
            onClick={() => onPageSizeChange?.(option)}
            onMouseEnter={() => setHoveredButton(option)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            {getLabel(option)}
          </button>
        ))}
      </div>
      
      <span style={STYLES.info}>
        Mostrando {visibleItems} de {totalItems} elementos
      </span>
    </div>
  );
};

export default Pagination;
